// Interface preferences behind the settings popover. Persisted in
// localStorage under one key each; every read tolerates missing storage.

const DEFAULTS = {
  hairline: false, // brand-green header hairline (off = plain border colour)
  mono: false, // monochrome: accent becomes white (dark) / black (light), logo greyscale
  sounds: true, // UI sound effects
  volume: 0.5, // 0..1 master level for UI sounds
  mix: 0.5, // custom theme: light weight of the palette, 0 = dark … 1 = light
  sidepanel: "expanded" as "expanded" | "rail", // side panel width while shown
  sidepanelHidden: false, // side panel hidden entirely (floating pill brings it back)
  touch: "auto" as "auto" | "on" | "off", // touch-scaled size tokens (auto = pointer: coarse)
} as const;

export type PrefKey = keyof typeof DEFAULTS;
type PrefValue<K extends PrefKey> = (typeof DEFAULTS)[K] extends boolean
  ? boolean
  : (typeof DEFAULTS)[K] extends number
    ? number
    : (typeof DEFAULTS)[K];

const KEY_PREFIX = "kitchenos.pref.";

export function getPref<K extends PrefKey>(key: K): PrefValue<K> {
  const fallback = DEFAULTS[key] as PrefValue<K>;
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage.getItem(KEY_PREFIX + key);
    if (raw === null) return fallback;
    if (typeof fallback === "boolean") return (raw === "1") as PrefValue<K>;
    if (typeof fallback === "string") return raw as PrefValue<K>;
    const n = Number(raw);
    return (Number.isFinite(n) ? n : fallback) as PrefValue<K>;
  } catch {
    return fallback;
  }
}

export function setPref<K extends PrefKey>(key: K, value: PrefValue<K>): void {
  try {
    const raw = typeof value === "boolean" ? (value ? "1" : "0") : String(value);
    window.localStorage.setItem(KEY_PREFIX + key, raw);
  } catch {
    // Storage unavailable — the preference holds for this session only.
  }
}
